import { Icon } from '@mui/material';
import { Avatar } from 'antd';
import { FunctionComponent, useEffect, useRef, useState } from 'react';

import { getPreviewAssignation } from '../../shared/api/assignationRequest';
import { isAssign, isEpLu, isEpUnlock } from '../../shared/api/serieRequest';
import { useAuth } from '../../shared/context/TokenContext';
import { Episode } from '../../shared/types/Episode';
import { Serie } from '../../shared/types/Serie';

import '../css/EpisodeRow.css';

export type EpisodeStatus = 'play' | 'pause' | 'finished';

interface EpisodeRowProps {
  episode: Episode;
  isAssigned: boolean;
  serie: Serie;
  seasonNumber: number;
  assign: (episode: Episode) => void;
  onClick: (episode: Episode, idSerie: number) => void;
  onStatusLoaded?: (episode: Episode, status: EpisodeStatus) => void;
}

export const getEpisodeStatus = (ep: Episode): EpisodeStatus => {
  if (!ep.EpLus || ep.EpLus.length <= 0) return 'play';

  const finished = ep.EpLus[0].finished;
  if (finished !== null && finished !== undefined) return finished === true ? 'finished' : 'pause';

  return 'play';
};

const STATUS_ACTION: Record<EpisodeStatus, { icon: string; label: string }> = {
  play: { icon: 'play_arrow', label: 'Lire' },
  pause: { icon: 'play_arrow', label: 'Reprendre' },
  finished: { icon: 'replay', label: 'Relire' },
};

const EpisodeRow: FunctionComponent<EpisodeRowProps> = (props: EpisodeRowProps) => {
  const { episode, isAssigned, serie, seasonNumber, assign, onClick, onStatusLoaded } = props;

  const { idClasse, isEdifice, isTeacher } = useAuth();

  const [assignements, setAssignements] = useState([]);
  const [count, setCount] = useState(0);
  const [ep, setEp] = useState(episode);
  const [isLoaded, setIsLoaded] = useState(false);

  const onStatusLoadedRef = useRef(onStatusLoaded);
  onStatusLoadedRef.current = onStatusLoaded;

  useEffect(() => {
    const loadInfos = async () => {
      const [resEpLu, resEpUnlock, resAssign, classe] = await Promise.all([
        isEpLu(`${episode.id}`),
        isEpUnlock(`${episode.id}`),
        isAssign(`${episode.id}`),
        idClasse ? getPreviewAssignation(episode.id, idClasse) : null,
      ]);

      const newEp = { ...episode };

      if (resEpLu) newEp.EpLus = resEpLu.epLu ? [resEpLu.epLu] : [];
      if (resEpUnlock) newEp.EpUnlockeds = resEpUnlock.epUnlocked ? [resEpUnlock.epUnlocked] : undefined;
      if (resAssign) newEp.isAssign = resAssign.assigned;

      setEp(newEp);
      setIsLoaded(true);
      onStatusLoadedRef.current?.(newEp, getEpisodeStatus(newEp));

      if (idClasse && classe && classe.students.length > 0) {
        setAssignements(classe.students);
        setCount(classe.count);
      }
    };
    loadInfos();
  }, [episode, idClasse, isAssigned]);

  const status = getEpisodeStatus(ep);
  const action = STATUS_ACTION[status];
  const isTeacherView = !!(isEdifice && isTeacher && isEdifice() && isTeacher());

  return (
    <li className={`ep-row ep-row--${status}`}>
      <button
        type="button"
        className="ep-row__main"
        onClick={() => onClick(ep, serie.id)}
        aria-label={`${action.label} l'épisode ${ep.number} : ${ep.name}`}>
        <span className="ep-row__thumb">
          <img
            src={`https://newbrand.s3.eu-west-3.amazonaws.com/series/${serie.uid}/season-${seasonNumber + 1}/ep-${
              ep.number
            }/img/${ep.img}`}
            alt=""
            loading="lazy"
          />
          {status === 'finished' ? (
            <span className="ep-row__done" aria-hidden="true">
              <Icon>check</Icon>
            </span>
          ) : null}
        </span>

        <span className="ep-row__info">
          <span className="ep-row__number">Épisode {ep.number}</span>
          <span className="ep-row__name">{ep.name}</span>
          <span className="ep-row__chips">
            {status === 'finished' ? <span className="ep-chip ep-chip--success">Lu</span> : null}
            {status === 'pause' ? <span className="ep-chip ep-chip--progress">En cours</span> : null}
            {ep.isAssign ? (
              <span className="ep-chip ep-chip--assigned">
                <Icon aria-hidden="true">notifications</Icon>
                Lecture demandée
              </span>
            ) : null}
          </span>
        </span>

        <span className={`ep-row__action${isLoaded ? '' : ' ep-row__action--pending'}`} aria-hidden="true">
          <Icon>{action.icon}</Icon>
          <span className="ep-row__action-label">{action.label}</span>
        </span>
      </button>

      {isTeacherView ? (
        <div className="ep-row__assign">
          {assignements.length > 0 ? (
            <Avatar.Group size="small">
              {assignements.map((a, i) => (
                <Avatar
                  key={i}
                  style={i % 2 === 0 ? { backgroundColor: '#FFD8BF', color: '#FA541C' } : { backgroundColor: '#FF7A45' }}>
                  {a}
                </Avatar>
              ))}
              {count > 0 ? (
                <Avatar
                  style={{
                    backgroundColor: assignements.length % 2 === 0 ? '#FFD8BF' : '#FF7A45',
                    color: assignements.length % 2 === 0 ? '#FA541C' : 'white',
                  }}>
                  +{count}
                </Avatar>
              ) : null}
            </Avatar.Group>
          ) : (
            <span className="ep-row__assign-empty">Pas encore assigné</span>
          )}
          <button
            type="button"
            className="ep-row__assign-btn"
            onClick={e => {
              e.stopPropagation();
              assign(ep);
            }}>
            <Icon aria-hidden="true">group_add</Icon>
            Assigner l’épisode
          </button>
        </div>
      ) : null}
    </li>
  );
};

export default EpisodeRow;
